import React from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import { club, heroImages, stats } from "../mock";
import VideoBackground from "./VideoBackground";
import HeroVideoLoop from "./HeroVideoLoop";
import HeroPlaylist from "./HeroPlaylist";
import { useSettings } from "../context/SettingsContext";

export default function Hero() {
  const settings = useSettings();
  const go = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const heroType = settings?.heroVideoType || "youtube";
  const heroId = settings?.heroVideoId || "0yxWOdNx2M0";
  const heroLoops = settings?.heroLoops || [];
  const heroLogoBetween = settings?.heroLogoBetween !== false;
  const todayET = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
  const matchDates = new Set([
    ...(settings?.matchDays || []),
    ...(settings?.fixtures || []).map((f) => f && f.date).filter(Boolean),
  ]);
  const isMatchDay = matchDates.has(todayET);
  const heroPlaylist = (settings?.heroPlaylist || [])
    .filter((c) => c && c.source)
    .filter((c) => {
      const s = c.schedule || "always";
      if (s === "match") return isMatchDay;
      if (s === "normal") return !isMatchDay;
      return true;
    });

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden">
      {/* Background video */}
      <div className="absolute inset-0">
        {heroPlaylist.length > 0 ? (
          <HeroPlaylist clips={heroPlaylist} poster={heroImages.main} logoBetween={heroLogoBetween} />
        ) : heroType === "youtube" ? (
          <HeroVideoLoop
            videoId={heroId}
            poster={heroImages.main}
            loops={heroLoops}
            logoBetween={heroLogoBetween}
          />
        ) : (
          <VideoBackground type={heroType} source={heroId} poster={heroImages.main} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 pb-24 pt-32">
        <div className="flex items-center gap-3 mb-5">
          <span className="h-[2px] w-10 bg-gold" />
          <span className="section-label text-gold text-xs">{club.tagline}</span>
        </div>

        <h1 className="display-font text-white uppercase leading-[0.92] text-5xl sm:text-7xl lg:text-8xl max-w-4xl">
          Built in South Florida.
          <br />
          <span className="text-gold">Driven to compete.</span>
        </h1>

        <p className="mt-6 text-white/75 text-lg max-w-xl font-light">
          A competitive young adult football club focused on high performance, discipline, and
          advancing players to the college and professional level.
        </p>

        <div className="mt-9 flex flex-wrap gap-4">
          <button
            onClick={() => go("#register")}
            className="group inline-flex items-center gap-2 bg-gold text-black cond-font uppercase tracking-widest font-semibold px-7 py-4 rounded-sm hover:bg-gold-light transition-colors"
          >
            Register for Tryouts
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={() => go("#about")}
            className="inline-flex items-center gap-2 border border-white/30 text-white cond-font uppercase tracking-widest font-medium px-7 py-4 rounded-sm hover:border-gold hover:text-gold transition-colors"
          >
            Learn More
          </button>
        </div>

        {/* Stats bar */}
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-3xl">
          {stats.map((s) => (
            <div key={s.label} className="bg-background/60 backdrop-blur-sm px-5 py-5">
              <div className="display-font text-3xl text-gold">{s.value}</div>
              <div className="cond-font uppercase text-[11px] tracking-widest text-white/60 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={() => go("#about")}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/50 hover:text-gold transition-colors animate-bounce"
      >
        <ChevronDown size={28} />
      </button>
    </section>
  );
}
